import { useMutation } from '@tanstack/react-query';
import { Button, Form, message } from 'antd';
import PageTitle from 'apps/home/components/PageTitle';
import apiCall from 'apps/investcore/apiCalls/home-widgets';
import calloutCardApiCall from 'apps/investcore/apiCalls/home-widget-callout-cards';
import { DetailPage, ListPage } from 'common/ui/AdminCRUD/page';
import LoadingSpinner from 'common/ui/LoadingSpinner';
import { useFetchApiGet, useFetchApiList } from 'hooks/api';
import { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import HomeWidgetForm from './form';
import ItemDetail from './detail';
import { columnsGenerator } from './config';

const CALLOUT_CARD_FIELD_KEYS = [
  'card_title',
  'card_description',
  'card_screen_name',
  'card_external_link',
  'card_theme',
  'card_photo',
  'card_button_label',
];

// The callout card content lives in a separate resource (home_widget_callout_cards)
// keyed by home_widget_id, so it can only be created once the parent widget has an
// ID — this splits the combined form's values back into the two payloads and saves
// them as one orchestrated submit instead of a separate detail-page step.
const splitCalloutCardFields = (values: Record<string, any>) => {
  const widgetValues = { ...values };
  const cardValues: Record<string, any> = {};
  for (const key of CALLOUT_CARD_FIELD_KEYS) {
    if (key in widgetValues) {
      cardValues[key.replace('card_', '')] = widgetValues[key];
      delete widgetValues[key];
    }
  }
  return { widgetValues, cardValues };
};

export const HomeWidgetListPage = (props, { params }) => (
  <ListPage
    enableSearch={true}
    title="Home Widgets"
    apiCall={apiCall}
    params={params}
    createUrl={`/admin/home-widgets/create`}
    columnGenerator={props => columnsGenerator(props)}
    resourceName="data"
    {...props}
  />
);

export const HomeWidgetDetailPage = props => (
  <DetailPage
    title="Home Widget"
    apiCall={apiCall}
    listUrl={`/admin/home-widgets`}
    ItemDetail={ItemDetail}
    resourceName="data"
    extraActions={id => (
      <>
        <Button type="primary">
          <Link to={`/admin/home-widgets/${id}/edit`}>Edit</Link>
        </Button>
      </>
    )}
    {...props}
  />
);

export const HomeWidgetCreatePage = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const { mutate, isPending } = useMutation({
    // Single atomic call — no orchestration here. Content that lives in a
    // separate resource (callout card / menu items / banners) needs a real
    // widget ID first, so it's filled in on the next screen instead of risking
    // a partial failure (widget created, content save fails) that a retry
    // would turn into a duplicate widget.
    mutationFn: (values: any) => {
      const { widgetValues } = splitCalloutCardFields(values);
      return apiCall.create.queryFn({ payload: widgetValues });
    },
    onSuccess: (created: any) => {
      message.success('Home widget created');
      const widgetId = created?.data?.id;
      const type = created?.data?.type;
      if (type === 'callout_card' && widgetId) {
        navigate(`/admin/home-widgets/${widgetId}/edit`);
      } else if ((type === 'menu' || type === 'banners') && widgetId) {
        navigate(`/admin/home-widgets/${widgetId}`);
      } else {
        navigate('/admin/home-widgets');
      }
    },
    onError: (err: any) => {
      message.error(err?.message || 'Failed to create home widget');
    },
  });

  return (
    <>
      <PageTitle title="Home Widget" backUrl="/admin/home-widgets" />
      <HomeWidgetForm
        form={form}
        submiting={isPending}
        onFinish={values => mutate(values)}
        name="create_form"
      />
    </>
  );
};

export const HomeWidgetEditPage = () => {
  const params = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const {
    data: widget,
    isLoading: isLoadingWidget,
    load: loadWidget,
  } = useFetchApiGet(apiCall.detail, { resourceName: 'data' });

  useEffect(() => {
    loadWidget(params);
  }, [JSON.stringify(params)]);

  const {
    data: cards,
    load: loadCards,
    isLoading: isLoadingCards,
  } = useFetchApiList(calloutCardApiCall.list, { resourceName: 'data' });

  useEffect(() => {
    if (widget?.type === 'callout_card' && widget?.id) {
      loadCards({ home_widget_id: widget.id });
    }
  }, [widget?.id, widget?.type]);

  const existingCard = widget?.type === 'callout_card' ? cards?.[0] : undefined;
  const isWaitingOnCard = widget?.type === 'callout_card' && isLoadingCards;

  const { mutate, isPending } = useMutation({
    mutationFn: async (values: any) => {
      const { widgetValues, cardValues } = splitCalloutCardFields(values);
      const updated = await apiCall.edit.queryFn({
        id: params.id,
        payload: widgetValues,
      });
      if (widgetValues.type === 'callout_card') {
        if (existingCard) {
          await calloutCardApiCall.edit.queryFn({
            id: existingCard.id,
            payload: cardValues,
          });
        } else {
          await calloutCardApiCall.create.queryFn({
            payload: { ...cardValues, home_widget_id: params.id },
          });
        }
      }
      return updated;
    },
    onSuccess: () => {
      message.success('Home widget updated');
      navigate('/admin/home-widgets');
    },
    onError: (err: any) => {
      message.error(err?.message || 'Failed to update home widget');
    },
  });

  if (isLoadingWidget || !widget || isWaitingOnCard) {
    return <LoadingSpinner />;
  }

  const initialValues =
    widget.type === 'callout_card'
      ? {
          ...widget,
          card_title: existingCard?.title,
          card_description: existingCard?.description,
          card_screen_name: existingCard?.screen_name,
          card_external_link: existingCard?.external_link,
          card_theme: existingCard?.theme,
          card_photo: existingCard?.photo,
          card_button_label: existingCard?.button_label,
        }
      : widget;

  return (
    <>
      <PageTitle title="Edit Home Widget" backUrl="/admin/home-widgets" />
      <HomeWidgetForm
        form={form}
        submiting={isPending}
        onFinish={values => mutate(values)}
        initialValues={initialValues}
        name="edit_form"
      />
    </>
  );
};
